import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { SERVER } from '../../../../config/config';
import { usePermission } from '../../../../hooks/usePermission';
import { 
  FaPlus, FaEdit, FaTrash, FaToggleOn, FaToggleOff, 
  FaSearch, FaSave, FaTimes, FaImage
} from 'react-icons/fa';

const HeroSections = () => {
  const { can } = usePermission();
  const [heroSections, setHeroSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingHero, setEditingHero] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    page_slug: '',
    title: '',
    subtitle: '',
    cta_label: '',
    cta_url: '',
    image: null,
    active: true
  });

  const canEdit = can('content.edit');
  const canCreate = can('content.create');
  const canDelete = can('content.delete');

  const pageOptions = [
    { value: 'home', label: 'Home Page' },
    { value: 'about', label: 'About Page' },
    { value: 'services', label: 'Services Page' },
    { value: 'portfolio', label: 'Portfolio Page' },
    { value: 'contact', label: 'Contact Page' },
    { value: 'studio', label: 'Creative Studio Page' }
  ];

  useEffect(() => {
    fetchHeroSections();
  }, [searchTerm]);

  const fetchHeroSections = async () => {
    try {
      setLoading(true);
      const params = {
        search: searchTerm
      };

      const response = await axios.get(`${SERVER}/hero-sections/admin/list`, {
        params,
        withCredentials: true
      });

      setHeroSections(response.data.hero_sections);
    } catch (error) {
      console.error('Error fetching hero sections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canCreate && !editingHero) return;
    if (!canEdit && editingHero) return;

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'image' && formData[key] instanceof File) {
          formDataToSend.append(key, formData[key]);
        } else if (key !== 'image') {
          formDataToSend.append(key, formData[key]);
        }
      });

      let response;
      if (editingHero) {
        response = await axios.put(`${SERVER}/hero-sections/admin/${editingHero.id}`, formDataToSend, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        response = await axios.post(`${SERVER}/hero-sections/admin`, formDataToSend, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      if (response.status === 200 || response.status === 201) {
        fetchHeroSections();
        closeModal();
      }
    } catch (error) {
      console.error('Error saving hero section:', error);
    }
  };

  const handleEdit = async (hero) => {
    if (!canEdit) return;
    
    setFormData({
      page_slug: hero.page_slug || '',
      title: hero.title || '',
      subtitle: hero.subtitle || '',
      cta_label: hero.cta_label || '',
      cta_url: hero.cta_url || '',
      image: null,
      active: hero.active
    });
    setEditingHero(hero);
    setShowModal(true);
  };

  const handleDelete = async (hero) => {
    if (!canDelete) return;
    
    if (window.confirm(`Are you sure you want to delete the hero section for "${hero.page_slug}"?`)) {
      try {
        await axios.delete(`${SERVER}/hero-sections/admin/${hero.id}`, {
          withCredentials: true
        });
        fetchHeroSections();
      } catch (error) {
        console.error('Error deleting hero section:', error);
      }
    }
  };

  const handleToggleActive = async (hero) => {
    if (!canEdit) return;
    
    try {
      await axios.put(`${SERVER}/hero-sections/admin/${hero.id}`, {
        ...hero,
        active: !hero.active
      }, {
        withCredentials: true
      });
      fetchHeroSections();
    } catch (error) {
      console.error('Error toggling hero section status:', error);
    }
  };

  const openCreateModal = () => {
    if (!canCreate) return;
    
    setFormData({
      page_slug: '',
      title: '',
      subtitle: '',
      cta_label: '',
      cta_url: '',
      image: null,
      active: true
    });
    setEditingHero(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingHero(null);
    setFormData({
      page_slug: '',
      title: '',
      subtitle: '',
      cta_label: '',
      cta_url: '',
      image: null,
      active: true
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Hero Sections</h2>
          <p className="text-slate-600">Manage hero sections for different pages</p>
        </div>
        {canCreate && (
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <FaPlus />
            <span>Add Hero Section</span>
          </button>
        )}
      </div>

      {/* Search */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-6">
        <div className="relative">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search hero sections..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Hero Sections Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {heroSections.map((hero) => (
          <div key={hero.id} className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
            {/* Hero Image */}
            {hero.image && (
              <div className="h-48 bg-gradient-to-br from-blue-500 to-indigo-600 relative overflow-hidden">
                <img
                  src={`${SERVER}${hero.image}`}
                  alt={hero.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/20" />
              </div>
            )}
            
            {/* Content */}
            <div className="p-6">
              <div className="flex items-center justify-between mb-3">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                  {pageOptions.find(p => p.value === hero.page_slug)?.label || hero.page_slug}
                </span>
                <button
                  onClick={() => handleToggleActive(hero)}
                  disabled={!canEdit}
                  className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                    hero.active
                      ? 'bg-green-100 text-green-800 hover:bg-green-200'
                      : 'bg-red-100 text-red-800 hover:bg-red-200'
                  } ${!canEdit ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                >
                  {hero.active ? <FaToggleOn /> : <FaToggleOff />}
                  {hero.active ? 'Active' : 'Inactive'}
                </button>
              </div>
              
              <h3 className="text-lg font-bold text-slate-800 mb-2 line-clamp-2">{hero.title}</h3>
              {hero.subtitle && (
                <p className="text-slate-600 text-sm mb-4 line-clamp-3">{hero.subtitle}</p>
              )}
              
              {hero.cta_label && (
                <div className="mb-4">
                  <span className="inline-flex items-center gap-2 px-3 py-1 bg-slate-100 text-slate-700 text-sm rounded-lg">
                    CTA: {hero.cta_label}
                  </span>
                </div>
              )}
              
              <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-200">
                <button
                  onClick={() => handleEdit(hero)}
                  disabled={!canEdit}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Edit"
                >
                  <FaEdit />
                </button>
                <button
                  onClick={() => handleDelete(hero)}
                  disabled={!canDelete}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Delete"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {heroSections.length === 0 && (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaImage className="text-3xl text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-2">No Hero Sections Found</h3>
          <p className="text-slate-600 mb-6">Create your first hero section to get started.</p>
          {canCreate && (
            <button
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300"
            >
              <FaPlus />
              Add Hero Section
            </button>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-800">
                  {editingHero ? 'Edit Hero Section' : 'Add New Hero Section'}
                </h3>
                <button
                  onClick={closeModal}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Page *
                </label>
                <select
                  value={formData.page_slug}
                  onChange={(e) => setFormData({ ...formData, page_slug: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select a page</option>
                  {pageOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter hero title"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Subtitle
                </label>
                <textarea
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter hero subtitle"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    CTA Button Text
                  </label>
                  <input
                    type="text"
                    value={formData.cta_label}
                    onChange={(e) => setFormData({ ...formData, cta_label: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Get Started"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    CTA Button URL
                  </label>
                  <input
                    type="url"
                    value={formData.cta_url}
                    onChange={(e) => setFormData({ ...formData, cta_url: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Background Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Status
                </label>
                <select
                  value={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.value === 'true' })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>

              <div className="flex items-center justify-end gap-4 pt-6 border-t border-slate-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-3 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <FaSave />
                  <span>{editingHero ? 'Update Hero Section' : 'Create Hero Section'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeroSections;